import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import LoadingSpinner from "@/components/loading-spinner";
import { useLocaleNavigation } from "@/hooks/use-locale-navigation";
import { useCartStore } from "@/components/cart/store";
import { OrderTypes } from "@/lib/supabase/enums";
import { resolveQrTableByToken } from "@/lib/qr";
import { useQrSessionStore } from "@/stores/qr-session";

/**
 * What a QR sticker on the table points at. Resolves the token to a table,
 * binds this browser tab to it and drops the guest straight on the menu — no
 * order type to pick, no details to fill in.
 */
export const Route = createFileRoute("/$locale/qr/t/$token")({
  loader: ({ params }) => resolveQrTableByToken({ data: { token: params.token } }),
  component: RouteComponent,
});

function RouteComponent() {
  const result = Route.useLoaderData();
  const { t } = useTranslation("translation");
  const { navigateWithLocale } = useLocaleNavigation();

  const setSession = useQrSessionStore((state) => state.setSession);
  const clearSession = useQrSessionStore((state) => state.clearSession);
  const setOrderType = useCartStore((state) => state.setOrderType);

  const token = Route.useParams().token;

  useEffect(() => {
    if (!result.success) {
      // A stale sticker must not leave the guest bound to whatever table they
      // scanned last.
      clearSession();
      // A code that is not on a table yet is still this venue's code: the guest
      // only has to say where they are sitting.
      if (result.code === "unassigned") {
        navigateWithLocale("/qr", { replace: true });
      }
      return;
    }

    setSession({
      tableId: result.table.id,
      tableReference: result.table.reference,
      sectionName: result.table.sectionName,
      token,
    });

    // QR ordering uses the take-away price list, so the rest of the app can
    // keep running its ordinary pickup path unchanged. Which products are on
    // the table menu is QR ordering's own availability (see selectQrMenu).
    setOrderType(OrderTypes.pickup);

    navigateWithLocale("/menu", { replace: true });
  }, [
    result,
    token,
    setSession,
    clearSession,
    setOrderType,
    navigateWithLocale,
  ]);

  if (result.success || result.code === "unassigned") {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
      <h1 className="text-2xl font-bold">
        {result.code === "disabled"
          ? t("Qr.disabled_title")
          : t("Qr.unknown_table_title")}
      </h1>
      <p className="max-w-md text-gray-600">
        {result.code === "disabled"
          ? t("Qr.disabled_description")
          : t("Qr.unknown_table_description")}
      </p>
    </div>
  );
}
